<template>
    <div class="flex flex-col gap-6 md:grid md:grid-cols-2 xl:grid-cols-3 md:items-start w-full">
        <!-- Left column: Suggestions -->
        <div class="min-w-0 flex flex-col gap-3 md:pr-6 md:border-r md:border-border md:self-start">
            <BaseCard padding="sm" class="flex flex-col gap-3">
                <div class="flex flex-col gap-1 min-w-0">
                    <h2 class="scaffold-body text-ink">Your goals</h2>
                    <p class="body-small text-ink-muted">Track other goals you're working towards. As you chat with Nadia, her suggestions will appear here.</p>
                    <BaseButton
                        v-if="showSuggestionsExplainer"
                        theme="tertiary"
                        size="sm"
                        class="text-left w-fit underline underline-offset-2"
                        @click="emit('explain-suggestions')">
                        How does Nadia suggest?
                    </BaseButton>
                </div>
                <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
                <section
                    aria-label="Suggestions drop zone"
                    class="relative rounded-xl transition-colors lg:flex lg:flex-col lg:flex-1 lg:min-h-0"
                    :class="suggestionsDropClasses"
                    @dragover="canDropOnSuggestions ? $event.preventDefault() : undefined"
                    @drop.prevent="handleDropOnSuggestions">
                    <div class="flex flex-col gap-3 max-h-[640px] overflow-y-auto lg:flex-1 lg:min-h-0">
                        <TransitionGroup v-if="allGoals.length > 0" tag="div" name="goal-card" class="relative flex flex-col gap-3">
                            <GoalCard
                                v-for="goal in allGoals"
                                :key="goal.id"
                                :goal="goal"
                                :is-loading="goalIsLoading(goal.id)"
                                :is-selected="goal.id === selectedGoalId"
                                :is-linked-hover="goal.id === hoveredResourceGoalId"
                                :is-suggestion="isSuggestedGoal(goal)"
                                draggable="true"
                                @dragstart="onDragStart(goal.id, isSuggestedGoal(goal) ? 'suggestions' : 'other_goals')"
                                @dragend="onDragEnd"
                                @select="emit('select', goal.id, 'suggestions')" />
                        </TransitionGroup>
                        <BaseCard
                            v-else
                            type="button"
                            as="button"
                            theme="muted"
                            :padding="hasAnyGoals ? 'sm' : 'lg'"
                            class="w-full flex flex-col items-center justify-center gap-2 lg:flex-1 lg:min-h-0"
                            :aria-label="hasAnyGoals ? 'Add a new goal' : 'Add your first goal'"
                            @click="emit('add-goal', 'other_goals')">
                            <template v-if="hasAnyGoals">
                                <i class="hidden md:block bi bi-arrows-move text-xl text-ink-muted" aria-hidden="true"></i>
                                <span class="md:hidden body-small text-ink-muted text-center font-medium">Add a new goal</span>
                            </template>
                            <span v-else class="body-small text-ink-muted text-center font-medium py-4">Add your first goal</span>
                        </BaseCard>
                    </div>
                </section>
            </BaseCard>
        </div>

        <!-- Right column on desktop; on mobile renders first (above suggestions), and is hidden entirely until at least one priority goal exists. -->
        <!-- On desktop: the right column can grow independently when additional sections render below priority goals. -->
        <div class="order-first md:order-none xl:col-span-2 flex flex-col gap-3 md:min-h-0">
            <BaseCard padding="sm" class="flex flex-col gap-3 w-full lg:flex-1 lg:min-h-0">
                <div class="flex flex-col gap-1 min-w-0">
                    <h2 class="scaffold-body text-ink">Priority goals</h2>
                    <p class="body-small text-ink-muted">Pick up to {{ maxPrioritizedGoals }} priority goals. Nadia will use these to enrich coaching.</p>
                    <BaseButton theme="tertiary" size="sm" class="self-start underline underline-offset-2" @click="emit('explain-priority')"> Why set priority goals? </BaseButton>
                </div>
                <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
                <section
                    aria-label="Priority goals drop zone"
                    class="rounded-xl transition-colors lg:flex lg:flex-col lg:flex-1 lg:min-h-0 lg:overflow-y-auto"
                    :class="[...journeyDropClasses, journeyGoals.length === 0 ? 'bg-surface-sunken' : '']"
                    @dragover="canPrioritize ? $event.preventDefault() : undefined"
                    @drop.prevent="handleDropOnPriority">
                    <TransitionGroup
                        v-if="journeyGoals.length > 0"
                        tag="div"
                        name="goal-card"
                        class="relative grid grid-cols-1 xl:grid-cols-2 gap-3 items-start xl:[&>*:last-child:nth-child(n+3):nth-child(odd)]:col-span-2 xl:[&>*:last-child:nth-child(n+3):nth-child(odd)]:justify-self-center xl:[&>*:last-child:nth-child(n+3):nth-child(odd)]:w-1/2">
                        <GoalCard
                            v-for="goal in journeyGoals"
                            :key="goal.id"
                            :goal="goal"
                            :is-loading="goalIsLoading(goal.id)"
                            :is-selected="goal.id === selectedGoalId"
                            :is-linked-hover="goal.id === hoveredResourceGoalId"
                            :is-discuss-loading="goalIsDiscussLoading(goal.id)"
                            show-discuss-button
                            draggable="true"
                            @dragstart="onDragStart(goal.id, 'priority_goals')"
                            @dragend="onDragEnd"
                            @select="emit('select', goal.id, 'priority_goals')"
                            @discuss="emit('discuss', $event)" />
                    </TransitionGroup>
                    <BaseCard
                        v-else
                        type="button"
                        as="button"
                        theme="muted"
                        padding="lg"
                        class="w-full flex flex-col items-center justify-center gap-2 lg:flex-1 lg:min-h-0"
                        :aria-label="hasAnyGoals ? 'Add a new priority goal' : 'Add your first priority goal'"
                        @click="emit('add-goal', 'priority_goals')">
                        <template v-if="hasAnyGoals">
                            <i class="hidden md:block bi bi-arrows-move text-xl text-ink-muted" aria-hidden="true"></i>
                            <span class="hidden md:block body-small text-ink-muted text-center font-medium">Drop a goal here, or click to add a new priority goal</span>
                            <span class="md:hidden body-small text-ink-muted text-center font-medium">Add your first prioritized goal</span>
                        </template>
                        <span v-else class="body-small text-ink-muted text-center font-medium">Add your first priority goal</span>
                    </BaseCard>
                </section>
            </BaseCard>
            <slot name="below-priority-goals" />
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import { type Goal } from "~vue/types/goals";
import { isSuggestedGoal, sortGoalsByDateDesc } from "~vue/utils/goals";
import { computed, ref } from "vue";

import GoalCard from "./GoalCard.vue";
import { type JourneysSection } from "./utils";

const props = withDefaults(
    defineProps<{
        suggestions: Goal[];
        journeyGoals: Goal[];
        selectedGoalId?: string | null;
        goalIsLoading: (goalId: string) => boolean;
        goalIsDiscussLoading: (goalId: string) => boolean;
        hoveredResourceGoalId?: string | null;
        maxPrioritizedGoals?: number;
        showSuggestionsExplainer?: boolean;
    }>(),
    {
        selectedGoalId: null,
        hoveredResourceGoalId: null,
        maxPrioritizedGoals: 3,
        showSuggestionsExplainer: true,
    },
);

const emit = defineEmits<{
    select: [goalId: string, fromSection: JourneysSection];
    drop: [goalId: string, fromSection: JourneysSection, toSection: JourneysSection];
    "explain-suggestions": [];
    "explain-priority": [];
    "drag-start": [goalId: string, fromSection: JourneysSection];
    "drag-end": [];
    discuss: [goalId: string];
    "add-goal": [target: "priority_goals" | "other_goals"];
}>();

const draggingGoalId = ref<string | null>(null);
const draggingFromSection = ref<JourneysSection | null>(null);

const allGoals = computed(() => sortGoalsByDateDesc(props.suggestions));
const hasAnyGoals = computed(() => allGoals.value.length > 0 || props.journeyGoals.length > 0);

const canPrioritize = computed(() => {
    const from = draggingFromSection.value;
    return (from === "suggestions" || from === "other_goals") && props.journeyGoals.length < props.maxPrioritizedGoals;
});

const canDropOnSuggestions = computed(() => draggingFromSection.value === "priority_goals");

const journeyDropClasses = computed(() => {
    return [canPrioritize.value ? "ring-2 ring-control-accent" : ""];
});

const suggestionsDropClasses = computed(() => {
    return canDropOnSuggestions.value ? "ring-2 ring-control-accent bg-select-option-selected" : "";
});

function onDragStart(goalId: string, fromSection: JourneysSection) {
    draggingGoalId.value = goalId;
    draggingFromSection.value = fromSection;
    emit("drag-start", goalId, fromSection);
}

function onDragEnd() {
    draggingGoalId.value = null;
    draggingFromSection.value = null;
    emit("drag-end");
}

function handleDropOnPriority() {
    const goalId = draggingGoalId.value;
    const from = draggingFromSection.value;
    const canDrop = canPrioritize.value;
    onDragEnd();
    if (!goalId || !from || !canDrop) {
        return;
    }
    emit("drop", goalId, from, "priority_goals");
}

function handleDropOnSuggestions() {
    const goalId = draggingGoalId.value;
    const from = draggingFromSection.value;
    onDragEnd();
    if (!goalId || from !== "priority_goals") {
        return;
    }
    emit("drop", goalId, from, "other_goals");
}
</script>

<style scoped>
.goal-card-enter-active {
    transition: opacity 250ms ease;
}
.goal-card-enter-from {
    opacity: 0;
}
.goal-card-leave-active {
    transition: opacity 200ms ease;
    position: absolute;
    width: 0;
    overflow: hidden;
}
.goal-card-leave-to {
    opacity: 0;
}
.goal-card-move {
    transition: transform 300ms ease;
}
</style>
